import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { onNewBuildAvailable } from "@/lib/buildVersion";


/**
 * Floating "Force refresh preview" control.
 *
 * Only rendered when the app is running inside an iframe (the Lovable editor
 * preview) or in local dev. Reloads the document with a fresh cache-busting
 * query param so a stale embedded browsing context can never serve old assets.
 */
const CACHE_BUST_PARAM = "_cb";
const AUTO_REFRESH_KEY = "gg.preview.autoRefresh";


/** App-owned storage namespaces that are safe to drop on a forced refresh. */
const APP_KEY_PREFIXES = ["gg.", "gg:", "gg_", "gg-", "ggl.", "gglife.", "gglife:"];

/**
 * Keys we deliberately keep: the auth session (never touched — anything
 * starting with `sb-`/`supabase`), plus durable user choices that would be
 * annoying to lose while previewing.
 */
const PRESERVED_KEYS = new Set([
  "gglife.theme",
  "darkMode",
  "highContrast",
  "gglife.region",
  "gg_cookie_consent",
  "gg_remembered_email",
  "arena_sso_next",
  "gg-current-org-id",
  AUTO_REFRESH_KEY,

]);

function isAuthKey(key: string) {
  return key.startsWith("sb-") || key.toLowerCase().includes("supabase");
}

function isClearable(key: string) {
  if (isAuthKey(key)) return false;
  if (PRESERVED_KEYS.has(key)) return false;
  return APP_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Clears cached/ephemeral app state the iframe may be holding (build-version
 * markers, route-flag cache, reader + admin UI toggles, signed-media cache,
 * in-progress AI/session scratch state) without signing the user out.
 */
function clearPreviewStorage() {
  const drop = (store: Storage) => {
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key && isClearable(key)) keys.push(key);
      }
      keys.forEach((key) => store.removeItem(key));
    } catch {
      // Storage blocked (private mode / sandboxed iframe) — ignore.
    }
  };
  drop(window.localStorage);
  // sessionStorage is per-frame scratch state, so clear every non-auth key.
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && !isAuthKey(key) && !PRESERVED_KEYS.has(key)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}


/**
 * Dev-only widget. It used to also show whenever the app was framed, which
 * meant it rendered on the published site inside any embed and floated over
 * the footer. Local development is the only place it may appear.
 */
function shouldShow() {
  if (typeof window === "undefined") return false;
  return import.meta.env.DEV;
}

export function ForceRefreshButton() {
  const [visible, setVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try {
      return window.localStorage.getItem(AUTO_REFRESH_KEY) === "1";
    } catch {
      return false;
    }
  });


  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  // Strip the cache-busting param from the visible URL after load so it never
  // leaks into shared links or router state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.has(CACHE_BUST_PARAM)) {
      url.searchParams.delete(CACHE_BUST_PARAM);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const forceRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {
      // Cache API unavailable or blocked — reload anyway.
    }
    clearPreviewStorage();
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
    window.location.replace(url.toString());
  }, []);

  // Optional auto-refresh: reload as soon as a new build (publish) or a dev
  // save (HMR update) lands, so the iframe can never sit on stale output.
  useEffect(() => {
    if (!visible || !autoRefresh) return;
    let cancelled = false;
    const trigger = () => {
      if (!cancelled) void forceRefresh();
    };

    const unsubscribeBuild = onNewBuildAvailable(trigger);

    let unsubscribeHmr: (() => void) | undefined;
    if (import.meta.hot) {
      // Debounce so a burst of saves results in one reload.
      let timer: number | undefined;
      const onUpdate = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(trigger, 400);
      };
      import.meta.hot.on("vite:afterUpdate", onUpdate);
      unsubscribeHmr = () => {
        window.clearTimeout(timer);
        import.meta.hot?.off("vite:afterUpdate", onUpdate);
      };
    }

    return () => {
      cancelled = true;
      unsubscribeBuild();
      unsubscribeHmr?.();
    };
  }, [visible, autoRefresh, forceRefresh]);

  const toggleAuto = (next: boolean) => {
    setAutoRefresh(next);
    try {
      window.localStorage.setItem(AUTO_REFRESH_KEY, next ? "1" : "0");
    } catch {
      // Storage blocked — the setting just won't persist.
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] print:hidden flex items-center gap-1 rounded-md border border-border/60 bg-secondary/80 p-1 shadow-lg backdrop-blur">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void forceRefresh()}
            disabled={refreshing}
            aria-label="Force refresh preview"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="ml-2 hidden sm:inline">Force refresh</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          Reload this preview with a cache-busting URL
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <label className="flex cursor-pointer items-center gap-1.5 pl-1 pr-2 text-xs text-muted-foreground">
            <Switch
              checked={autoRefresh}
              onCheckedChange={toggleAuto}
              aria-label="Auto-refresh preview on every save or publish"
            />
            <span className="hidden sm:inline">Auto</span>
          </label>
        </TooltipTrigger>
        <TooltipContent side="left">
          Auto-refresh whenever a save or publish lands
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

